import * as React from 'react'
import * as utils from '../utils'
import * as t from '../types'
import useAccount from '../useAccount'
import transformOrder from '../order/transformOrder'
import { GetOrdersResponse } from '../response-types/get-orders'

type Props = {
  pageSize?: number
}

export default function useOrderList(props: Props) {
  const account = useAccount()
  const [orders, setOrders] = React.useState(account._.stores.orderList || null)
  const [isFetching, setIsFetching] = React.useState(!orders)
  const [hasNextBatch, setHasNextBatch] = React.useState(false)
  const [page, setPage] = React.useState(1)
  const [setup, setSetup] = React.useState(!!account._.stores.orderList)
  const load = async (limit?: number) => {
    setIsFetching(true)

    const promise = fetchOrders({
      page: limit ? 1 : page,
      limit: limit || props.pageSize || 6,
      'total-count-mode': 1,
      sort: [{ field: 'orderDate', order: 'DESC', naturalSorting: true }],
      loggedIn: account.context?.loggedIn || false
    })

    account._.stores.orderListP = promise
    const response = await promise

    if (response.status === 200) {
      setHasNextBatch(response.payload.hasNextBatch)
      if (page == 1) {
        setOrders([...response.payload.orders])
      } else {
        setOrders((prev) => [...(prev || []), ...response.payload.orders])
      }
      setPage(response.payload.page + 1)
      account._.stores.orderList = response.payload.orders
    }

    delete account._.stores.orderListP
    setIsFetching(false)
    if (!setup) setSetup(true)
  }

  React.useEffect(() => {
    if (!account.loggedIn) return
    if (account.context) load()

    return account._.onEvent((evt) => {
      switch (evt.type) {
        case 'LOGOUT':
          setOrders([])
          break
        case 'LOGIN':
        case 'REGISTER':
        case 'CREATE_ORDER':
          load()
      }
    })
  }, [account.context, account.loggedIn])

  return {
    data: orders,
    isFetching,
    setup,
    fetchNextBatch: () => load(),
    hasNextBatch: hasNextBatch,
    fetchBatchByLimit: (limit) => {
      setOrders(() => null)
      load(limit)
    }
  }
}

async function fetchOrders(options: {
  page: number
  limit: number
  'total-count-mode': number
  sort: any[]
  // only used for error tracking
  loggedIn: boolean
}): t.Response<{ orders: t.Order[]; hasNextBatch: boolean; page: number }> {
  try {
    const [result, status] = await utils.fetchFromShopware<GetOrdersResponse>({
      method: 'POST',
      url: '/order',
      body: {
        associations: {
          tags: [],
          transactions: {
            associations: {
              stateMachineState: []
            }
          },
          deliveries: {
            associations: {
              shippingOrderAddress: [],
              stateMachineState: []
            }
          },
          stateMachineState: [],
          lineItems: ['quantity']
        },
        ...options
      }
    })

    if (status >= 400) {
      throw new Error('FETCH_ORDERS_FAILED:USE_ORDER_LIST')
    }

    const orders: t.Order[] = []

    for (const el of result.orders.elements) {
      orders.push(transformOrder(el))
    }
    return {
      status: 200,
      payload: {
        orders,
        hasNextBatch:
          result.orders.limit * result.orders.page < result.orders.total,
        page: result.orders.page
      }
    }
  } catch (e: any) {
    return { status: 500, payload: e.toString() }
  }
}
